/**
 * OAuth Callback Handler
 *
 * Handles OAuth redirect from Facebook/Google/etc.
 * Amplify Auth automatically exchanges the authorization code for tokens
 * This component listens to Hub events for OAuth completion
 */

import { useEffect, useState } from 'react';
import { Center, Loader, Stack, Text, Alert } from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser } from 'aws-amplify/auth';
import { IconAlertCircle } from '@tabler/icons-react';
import { trackLoginCompleted } from '../../utils/analytics';
import { getPreAuthOSD, clearPreAuthOSD } from '../../utils/auth/osdPreservation';
import { useSEO } from '../../shared/hooks/useSEO';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');

  useSEO({ title: 'Autoryzacja', noindex: true });

  useEffect(() => {
    // Development only logging

    // Check if we have OAuth parameters
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    setDebugInfo(`Code: ${code?.substring(0, 8)}..., State: ${state?.substring(0, 8)}...`);

    // Handle OAuth error from URL
    if (errorParam) {
      setError(errorDescription || 'Błąd autoryzacji OAuth');
      notifications.show({
        title: 'Błąd logowania',
        message: errorDescription || 'Nie udało się zalogować przez Facebook',
        color: 'red',
      });
      // Preserve OSD context when redirecting to login on error
      const savedOSD = getPreAuthOSD();
      const loginPath = savedOSD ? `/${savedOSD}/login` : '/login';
      setTimeout(() => navigate(loginPath, { replace: true }), 3000);
      return;
    }

    // If we don't have code/state, this is not an OAuth callback
    if (!code || !state) {
      // Development only logging
      setError('Brak parametrów OAuth (code/state)');
      const savedOSD = getPreAuthOSD();
      const loginPath = savedOSD ? `/${savedOSD}/login` : '/login';
      setTimeout(() => navigate(loginPath, { replace: true }), 2000);
      return;
    }

    // Development only logging

    // Listen for Hub events from Amplify Auth
    const hubListenerCancelToken = Hub.listen('auth', async ({ payload }) => {
      // Development only logging

      switch (payload.event) {
        case 'signInWithRedirect':
          // Development only logging
          try {
            const user = await getCurrentUser();
            // Development only logging

            // Track successful Facebook login
            trackLoginCompleted('facebook', user.userId);

            // Show success notification
            notifications.show({
              title: 'Zalogowano pomyślnie!',
              message: 'Witamy w Taryfy Prądu',
              color: 'green',
            });

            // Redirect to dashboard with OSD prefix
            const savedOSD = getPreAuthOSD() || 'TAURON';
            clearPreAuthOSD();
            navigate(`/${savedOSD}/konto/panel`, { replace: true });
          } catch (err) {
            setError('Nie udało się pobrać danych użytkownika');
          }
          break;

        case 'signInWithRedirect_failure': {
          const errorData = payload.data as { error?: { message?: string } };
          const errorMessage = errorData?.error?.message || 'Nie udało się zalogować przez Facebook';
          setError(errorMessage);
          notifications.show({
            title: 'Błąd logowania',
            message: errorMessage,
            color: 'red',
          });
          const savedOSD = getPreAuthOSD();
          const loginPath = savedOSD ? `/${savedOSD}/login` : '/login';
          setTimeout(() => navigate(loginPath, { replace: true }), 3000);
          break;
        }

        case 'tokenRefresh_failure': {
          setError('Nie udało się odświeżyć sesji');
          const savedOSD = getPreAuthOSD();
          const loginPath = savedOSD ? `/${savedOSD}/login` : '/login';
          setTimeout(() => navigate(loginPath, { replace: true }), 3000);
          break;
        }

        default:
          // Development only logging
      }
    });

    // Fallback: Check auth status after timeout (in case Hub events don't fire)
    const fallbackTimer = setTimeout(async () => {
      // Development only logging
      try {
        const user = await getCurrentUser();
        // Development only logging

        // Track successful Facebook login
        trackLoginCompleted('facebook', user.userId);

        notifications.show({
          title: 'Zalogowano pomyślnie!',
          message: 'Witamy w Taryfy Prądu',
          color: 'green',
        });

        // Redirect to dashboard with OSD prefix
        const savedOSD = getPreAuthOSD() || 'TAURON';
        clearPreAuthOSD();
        navigate(`/${savedOSD}/konto/panel`, { replace: true });
      } catch (err) {
        setError('Przekroczono limit czasu autoryzacji. Spróbuj ponownie.');
        const savedOSD = getPreAuthOSD();
        const loginPath = savedOSD ? `/${savedOSD}/login` : '/login';
        setTimeout(() => navigate(loginPath, { replace: true }), 2000);
      }
    }, 10000);

    // Cleanup
    return () => {
      // Development only logging
      hubListenerCancelToken();
      clearTimeout(fallbackTimer);
    };
  }, [searchParams, navigate]);

  return (
    <Center style={{ height: '100vh', padding: '20px' }}>
      <Stack align="center" gap="md" style={{ maxWidth: '500px' }}>
        {error ? (
          <>
            <Alert
              icon={<IconAlertCircle size={24} />}
              title="Błąd autoryzacji"
              color="red"
              style={{ width: '100%' }}
            >
              {error}
              <Text size="xs" c="dimmed" mt="sm">
                {debugInfo}
              </Text>
            </Alert>
            <Text size="sm" c="dimmed">
              Przekierowanie do strony logowania...
            </Text>
          </>
        ) : (
          <>
            <Loader size="xl" />
            <Text size="lg" fw={500}>
              Logowanie...
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Trwa uwierzytelnianie z Facebook.
              <br />
              To może potrwać kilka sekund.
            </Text>
            <Text size="xs" c="dimmed" mt="md">
              {debugInfo}
            </Text>
          </>
        )}
      </Stack>
    </Center>
  );
}
